// phone-input.component.ts
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  Validators,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CountryStateService } from 'src/app/services/client/country-state.service';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor {
  @Input() label = 'Teléfono';
  @Input() availablePrefixes = ['+34', '+33', '+44'];
  @Input() formControl!: FormControl;
  @Input() controlName!: string;
  @Output() countryChanged = new EventEmitter<string>();
  placeholderExample = 'Ej: 612 34 56 78';

  touched = false;
  errorMessage: string | null = null;

  prefixCtrl = new FormControl('+34');
  nationalCtrl = new FormControl('');
  formattedMaxLength = 14;

  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private countryService: CountryStateService) {}

  ngOnInit(): void {
    this.subscribeToChanges();
  }

  private mapPrefixToCountry(prefix: string): string {
    const isoMap: Record<string, string> = {
      '+34': 'ES',
      '+33': 'FR',
      '+44': 'GB',
    };
    return isoMap[prefix] || 'ES';
  }

  writeValue(value: string): void {
    if (!value) return;

    const prefix =
      this.availablePrefixes.find((p) => value.startsWith(p)) || '+34';
    const national = value.replace(prefix, '').replace(/\D/g, '');

    this.prefixCtrl.setValue(prefix, { emitEvent: false });
    this.nationalCtrl.setValue(national, { emitEvent: false });

    // 🔥 Forzar validación y touched para que salten errores si son necesarios
    setTimeout(() => {
      this.formControl?.markAsTouched();
      this.formControl?.updateValueAndValidity();
    });

    // 🔁 También forzamos emitir el valor formateado si es necesario
    if (this.onChange) {
      this.onChange(`${prefix}${national}`);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(): null {
    return null; // sin validación interna, ya se hace en el padre
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.prefixCtrl.disable();
      this.nationalCtrl.disable();
    } else {
      this.prefixCtrl.enable();
      this.nationalCtrl.enable();
    }
  }

  private subscribeToChanges(): void {
    this.prefixCtrl.valueChanges.subscribe((prefix) => {
      this.updatePlaceholder(prefix ?? '');
      const iso = this.mapPrefixToCountry(prefix ?? '');
      this.countryService.setSelectedCountry(iso);
      const digits = (this.nationalCtrl.value ?? '')
        .replace(/\D/g, '')
        .slice(0, this.getMaxLength(prefix ?? ''));
      this.nationalCtrl.setValue(this.formatNational(prefix ?? '', digits), {
        emitEvent: false,
      });
      this.formattedMaxLength = this.getFormattedMaxLength(prefix ?? '');
    });

    this.nationalCtrl.valueChanges.subscribe((value) => {
      const prefix = this.prefixCtrl.value;
      const max = this.getMaxLength(prefix ?? '');
      const raw = (value || '').replace(/\D/g, '').slice(0, max);
      const formatted = this.formatNational(prefix ?? '', raw);
      this.nationalCtrl.setValue(formatted, { emitEvent: false });
      this.onChange(`${prefix}${raw}`);
      // this.evaluateErrorMessage();
    });
  }

  updatePlaceholder(prefix: string): void {
    switch (prefix) {
      case '+34':
        this.placeholderExample = 'Ej: 612 34 56 78';
        break;
      case '+33':
        this.placeholderExample = 'Ex: 6 12 34 56 78';
        break;
      case '+44':
        this.placeholderExample = 'Eg: 1234 567 890';
        break;
      default:
        this.placeholderExample = 'Phone number';
    }
  }

  private getMaxLength(prefix: string): number {
    switch (prefix) {
      case '+34':
      case '+33':
        return 10;
      case '+44':
        return 10;
      default:
        return 9;
    }
  }

  private getFormattedMaxLength(prefix: string): number {
    switch (prefix) {
      case '+34':
        return 12;
      case '+33':
        return 14;
      case '+44':
        return 13;
      default:
        return 14;
    }
  }

  private formatNational(prefix: string, raw: string): string {
    switch (prefix) {
      case '+34':
        return raw.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4');
      case '+33':
        return raw.replace(
          /(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/,
          '$1 $2 $3 $4 $5'
        );
      case '+44':
        return raw.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
      default:
        return raw;
    }
  }

  blockNonNumeric(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
    if (!/^[0-9]$/.test(event.key) && !allowed.includes(event.key)) {
      event.preventDefault();
    }
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.evaluateErrorMessage(); // 🔁 evalúa error tras salir del input
  }

  validateNationalNumber(prefix: string, number: string): boolean {
    switch (prefix) {
      case '+34':
        return /^[679]\d{8}$/.test(number);
      case '+33':
        return /^[0]\d{9}$/.test(number);
      case '+44':
        return /^\d{10}$/.test(number);
      default:
        return false;
    }
  }

  getInvalidMessage(prefix: string): string {
    switch (prefix) {
      case '+34':
        return 'Número español no válido';
      case '+33':
        return 'Numéro français invalide';
      case '+44':
        return 'UK phone number invalid';
      default:
        return 'Formato de teléfono no válido';
    }
  }

  evaluateErrorMessage(): void {
    if (this.controlName === 'phoneNumber') {
      this.errorMessage = null;
      return;
    }

    const prefix = this.prefixCtrl.value;
    const raw = (this.nationalCtrl.value || '').replace(/\D/g, '');

    if (!this.touched) {
      this.errorMessage = null;
      return;
    }

    if (!raw) {
      this.errorMessage = 'El teléfono es obligatorio';
    } else if (!this.validateNationalNumber(prefix ?? '', raw)) {
      this.errorMessage = this.getInvalidMessage(prefix ?? '');
    } else {
      this.errorMessage = null;
    }
  }
}
